import React, { useEffect, useMemo, useRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import MultiSelectCst from "@components/InputCst/MultiSelectCst";
import StatusBadgeCst from "@components/StatusBadgeCst";
import { WizardStepProps } from "@components/WizardCst/types";
import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { UserWizardData } from "../types";
import {
  FieldGrid,
  SectionDivider,
  SectionHeader,
  StepContainer,
} from "../components/StepLayout";
import QuestionToggle from "../components/QuestionToggle";

const MainInfoStep: React.FC<WizardStepProps<UserWizardData>> = ({
  data,
  setData,
  errors,
  mode,
}) => {
  const { t } = useTranslation("user-wizard-v2");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const err = (key: string) =>
    errors[key] ? t(`validation.${errors[key]}`) : "";

  const { trigger: fetchLanguages, data: corporateLanguages } = useSWRMutation(
    ServicesURL.corporateLanguages.replace(":corporateId", corporateId),
    (url: string) => fetcher(url, MethodType.GET),
  );

  useEffect(() => {
    if (corporateId) fetchLanguages();
  }, [corporateId]);

  const languageOptions = useMemo(() => {
    if (!corporateLanguages) return [];
    return [
      corporateLanguages.primary_language,
      ...(corporateLanguages.secondary_languages || []),
    ]
      .filter(Boolean)
      .map((l: any) => ({ value: l.id, label: l.label }));
  }, [corporateLanguages]);

  const hasSetDefaultLanguage = useRef(false);
  useEffect(() => {
    if (hasSetDefaultLanguage.current) return;
    const primaryId = corporateLanguages?.primary_language?.id;
    if (primaryId && !data.preference_language) {
      setData({ preference_language: primaryId.toString() });
      hasSetDefaultLanguage.current = true;
    }
  }, [corporateLanguages, data.preference_language, setData]);

  return (
    <StepContainer>
      <FieldGrid>
        <InputCst
          id="user-name"
          label={t("fields.name")}
          value={data.name}
          onChange={(e: any) => setData({ name: e.target.value })}
          error={!!errors.name}
          errorMessage={err("name")}
        />
        <InputCst
          id="user-surname"
          label={t("fields.surname")}
          value={data.surname}
          onChange={(e: any) => setData({ surname: e.target.value })}
          error={!!errors.surname}
          errorMessage={err("surname")}
        />
        <InputCst
          id="user-external-id"
          label={t("fields.externalId")}
          value={data.external_id}
          onChange={(e: any) => setData({ external_id: e.target.value })}
          error={!!errors.external_id}
          errorMessage={err("external_id")}
          disabled={mode === "edit"}
        />
        <InputCst
          id="user-email"
          label={t("fields.email")}
          value={data.email}
          onChange={(e: any) => setData({ email: e.target.value })}
          error={!!errors.email}
          errorMessage={err("email")}
        />
      </FieldGrid>

      <FieldGrid>
        <InputCst
          id="user-username"
          label={t("fields.username")}
          value={data.username}
          onChange={(e: any) => setData({ username: e.target.value })}
          error={!!errors.username}
          errorMessage={err("username")}
        />

        <SelectCst
          id="user-preference-language"
          label={t("fields.preferenceLanguage")}
          value={data.preference_language}
          options={languageOptions}
          required
          error={!!errors.preference_language}
          errorMessage={err("preference_language")}
          onChange={(value: any) => setData({ preference_language: value })}
        />
      </FieldGrid>

      <SectionDivider />

      <SectionHeader
        title={t("sections.rolesPermissions")}
        description={t("sections.rolesPermissionsDesc")}
      />
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "stretch",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, mb: 1 }}>
            {t("fields.roles")}
          </Typography>
          <MultiSelectCst
            id="user-roles"
            label={t("fields.addRole")}
            value={data.roles}
            onChange={(value: any) => setData({ roles: value })}
            options={[]}
            service="getRoles"
            required
            error={!!errors.roles}
            errorMessage={err("roles")}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 280,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              mb: 1,
              visibility: "hidden",
            }}
          >
            &nbsp;
          </Typography>
          <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
            <QuestionToggle
              question={t("questions.adminPermissions")}
              checked={data.admin_flag}
              onChange={(next) => setData({ admin_flag: next })}
              noLabel={t("common.no")}
              yesLabel={t("common.yes")}
            />
          </Box>
        </Box>
      </Box>

      <SectionDivider />

      <SectionHeader
        title={t("sections.userManagement")}
        description={t("sections.userManagementDesc")}
      />

      <QuestionToggle
        question={t("questions.appCredentials")}
        description={t("questions.appCredentialsDesc")}
        checked={data.external_employee_flag}
        onChange={(next) => setData({ external_employee_flag: next })}
        noLabel={t("common.no")}
        yesLabel={t("common.yes")}
      />

      <QuestionToggle
        question={t("questions.receivedCredentials")}
        description={t("questions.receivedCredentialsDesc")}
        checked={data.receieved_credentials_flag}
        onChange={(next) => setData({ receieved_credentials_flag: next })}
        noLabel={t("common.no")}
        yesLabel={t("common.yes")}
      />

      <QuestionToggle
        question={t("questions.privacyConsent")}
        description={t("questions.privacyConsentDesc")}
        checked={data.privacy_consent_flag}
        onChange={(next) => setData({ privacy_consent_flag: next })}
        noLabel={t("common.no")}
        yesLabel={t("common.yes")}
      />

      <QuestionToggle
        question={t("questions.professionalLevel")}
        description={t("questions.professionalLevelDesc")}
        checked={data.professional_level === "MANAGER"}
        onChange={(next) =>
          setData({ professional_level: next ? "MANAGER" : "PROFESSIONAL" })
        }
        noLabel={t("questions.professional")}
        yesLabel={t("questions.manager")}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {t("status.label")}
          </Typography>
          <StatusBadgeCst label={t("status.active")} variant="active" />
        </Box>
        <Typography
          sx={{ fontSize: "13px", color: theme.palette.text.primary }}
        >
          {t("status.desc")}
        </Typography>
      </Box>
    </StepContainer>
  );
};

export default MainInfoStep;
